import { api, Api } from '@/services/api';
import type {
  AddParticipants,
  AddParticipantsParams,
  ArenaTournament,
  ListResult,
  MyTournament,
  MyTournamentsParams,
  Tournament,
  TournamentDetails,
  TournamentParticipants,
  TournamentRules,
  TournamentsParams,
} from '@/types';

const tournamentsEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    myTournaments: builder.query<ListResult<MyTournament>, MyTournamentsParams>({
      query: (params) => ({
        url: Api.MyTournaments,
        params,
      }),
      providesTags: ['Tournaments'],
    }),
    userTournaments: builder.query<TournamentDetails, number>({
      query: (id) => ({
        url: Api.UserTournaments.replace(':id', id.toString()),
      }),
    }),
    tournaments: builder.query<ListResult<Tournament>, TournamentsParams>({
      query: (params) => ({
        url: Api.Tournaments,
        params,
      }),
      providesTags: ['Tournaments'],
    }),
    tournamentsArena: builder.query<{ data: ArenaTournament[] }, { id: number }>({
      query: ({ id }) => ({
        url: Api.TournamentsArena.replace(':id', id.toString()),
      }),
    }),
    tournamentId: builder.query<TournamentDetails, number>({
      query: (id) => ({
        url: Api.TournamentId.replace(':id', id.toString()),
      }),
      providesTags: (_r, _e, id) => [{ type: 'Tournaments', id }],
    }),
    tournamentRules: builder.query<TournamentRules, number>({
      query: (id) => ({
        url: Api.TournamentRules.replace(':id', id.toString()),
      }),
    }),
    tournamentParticipants: builder.query<TournamentParticipants, number>({
      query: (id) => ({
        url: Api.TournamentParticipants.replace(':id', id.toString()),
      }),
    }),
    addParticipants: builder.mutation<AddParticipants, AddParticipantsParams>({
      query: (params) => ({
        url: Api.TournamentParticipants.replace(':id', params.id),
        method: 'POST',
        body: { secret_code: params.secret_code, team_id: params.team_id },
      }),
      invalidatesTags: ['Tournaments'],
    }),
    setActiveParticipant: builder.mutation<void, AddParticipantsParams>({
      query: (params) => ({
        url: Api.TournamentParticipants.replace(':id', params.id),
        method: 'PUT',
      }),
      invalidatesTags: ['Tournaments'],
    }),
    removeParticipants: builder.mutation<void, { id: string; participantId: string }>({
      query: (params) => ({
        url: Api.RemoveParticipant.replace(':id', params.id).replace(
          ':participant_id',
          params.participantId,
        ),
        method: 'DELETE',
      }),
      invalidatesTags: ['Tournaments'],
    }),
  }),
});

export const {
  useMyTournamentsQuery,
  useUserTournamentsQuery,
  useTournamentsQuery,
  useTournamentsArenaQuery,
  useTournamentIdQuery,
  useTournamentRulesQuery,
  useTournamentParticipantsQuery,
  useAddParticipantsMutation,
  useSetActiveParticipantMutation,
  useRemoveParticipantsMutation,
} = tournamentsEndpoints;
